// BOGO free item handler
(function () {
  const FREE_ITEM_PROPERTY = '_bogo_free_item';
  const FREE_ITEM_MARKER = '_bogo_free';
  const API_ROOT = window.Shopify?.routes?.root || '/';
  let isProcessing = false;
  const freeItemCache = new Map();
  const storefrontToken = window.themeVariables?.settings?.storefrontApiToken;

  const fetchStorefrontMetafield = async (variantId) => {
    if (!storefrontToken) {
      console.warn('[BOGO] Storefront API token is missing. Add it in theme settings to enable BOGO fallback.');
      return null;
    }

    if (!variantId) {
      console.warn('[BOGO] Missing variant id; cannot resolve free item.');
      return null;
    }

    const gid = `gid://shopify/ProductVariant/${variantId}`;

    const response = await fetch(`${API_ROOT}api/2024-01/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Shopify-Storefront-Access-Token': storefrontToken,
      },
      body: JSON.stringify({
        query: `query GetBogoMetafield($id: ID!) {\n  node(id: $id) {\n    ... on ProductVariant {\n      metafield(namespace: \"custom\", key: \"bogo_free_item\") {\n        type\n        value\n      }\n    }\n  }\n}`,
        variables: { id: gid },
      }),
    });

    if (!response.ok) {
      throw new Error('Unable to load Storefront API response');
    }

    const payload = await response.json();
    const metafieldValue = payload?.data?.node?.metafield?.value;
    const metafieldType = payload?.data?.node?.metafield?.type;
    if (!metafieldValue) {
      return null;
    }

    if (metafieldType === 'variant_reference') {
      const match = String(metafieldValue).match(/ProductVariant\/(\d+)/);
      if (match) {
        return match[1];
      }
      console.warn('[BOGO] Unable to parse variant_reference GID', metafieldValue);
      return null;
    }

    try {
      const parsed = JSON.parse(metafieldValue);
      if (typeof parsed === 'object' && parsed?.id) {
        return parsed.id;
      }
      return parsed;
    } catch (error) {
      return metafieldValue;
    }
  };

  const resolveFreeItemId = async (item) => {
    if (!item || item?.properties?.[FREE_ITEM_MARKER]) {
      return null;
    }

    const variantId = item?.id;
    if (!variantId) {
      return null;
    }

    const cacheKey = String(variantId);
    if (freeItemCache.has(cacheKey)) {
      return freeItemCache.get(cacheKey);
    }

    const freeItemIdFromProperties = item?.properties?.[FREE_ITEM_PROPERTY];
    if (freeItemIdFromProperties) {
      const normalized = String(freeItemIdFromProperties);
      freeItemCache.set(cacheKey, normalized);
      return normalized;
    }

    try {
      const freeItemId = await fetchStorefrontMetafield(variantId);
      const normalized = freeItemId ? String(freeItemId) : null;
      freeItemCache.set(cacheKey, normalized);
      return normalized;
    } catch (error) {
      freeItemCache.set(cacheKey, null);
      return null;
    }
  };

  const fetchCart = async () => {
    const response = await fetch(`${API_ROOT}cart.js`);
    if (!response.ok) {
      throw new Error('Unable to load cart');
    }
    return response.json();
  };

  const getEligibleFreeItemIds = async (cart) => {
    const ids = new Set();
    await Promise.all(
      cart.items.map(async (item) => {
        const freeItemId = await resolveFreeItemId(item);
        if (freeItemId && item?.selling_plan_allocation) {
          ids.add(String(freeItemId));
        }
      }),
    );
    return ids;
  };

  const getFreeLines = (cart) => cart.items.filter((item) => item?.properties?.[FREE_ITEM_MARKER]);

  const addFreeItem = async (variantId) => {
    await fetch(`${API_ROOT}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: Number(variantId),
        quantity: 1,
        properties: {
          [FREE_ITEM_MARKER]: 'true',
        },
      }),
    });
  };

  const setLineQuantity = async (lineKey, quantity) => {
    await fetch(`${API_ROOT}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: lineKey,
        quantity,
      }),
    });
  };

  const syncFreeItems = async (cart) => {
    if (isProcessing) {
      return;
    }
    isProcessing = true;
    try {
      const cartData = cart || (await fetchCart());
      const eligibleIds = await getEligibleFreeItemIds(cartData);
      const freeLines = getFreeLines(cartData);

      const existingFreeIds = new Set(freeLines.map((line) => String(line.id)));
      const missingFreeIds = Array.from(eligibleIds).filter((id) => !existingFreeIds.has(id));
      const obsoleteFreeLines = freeLines.filter((line) => !eligibleIds.has(String(line.id)));
      const quantityMismatchLines = freeLines.filter((line) => eligibleIds.has(String(line.id)) && line.quantity !== 1);

      for (const variantId of missingFreeIds) {
        if (!variantId) {
          continue;
        }
        await addFreeItem(variantId);
      }

      for (const line of obsoleteFreeLines) {
        await setLineQuantity(line.key, 0);
      }

      for (const line of quantityMismatchLines) {
        await setLineQuantity(line.key, 1);
      }

      if (missingFreeIds.length || obsoleteFreeLines.length || quantityMismatchLines.length) {
        document.dispatchEvent(new CustomEvent('cart:refresh'));
      }
    } catch (error) {
      console.warn('[BOGO] Unable to sync free items', error);
    } finally {
      isProcessing = false;
    }
  };

  document.addEventListener('cart:change', (event) => syncFreeItems(event.detail?.cart));
  document.addEventListener('cart:refresh', () => syncFreeItems());
  window.addEventListener('pageshow', () => syncFreeItems());
})();
